// /identities/:id/edit — editar cliente

import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { UpdateClientDto } from '@control-center/shared';

import { ClientForm } from '../../components/clients/ClientForm';
import { ClientTableSkeleton } from '../../components/clients/ClientTable';
import { PillButton } from '../../components/clients/PillButton';
import { useClient, useUpdateClient } from '../../hooks/use-clients';
import { extractErrorMessage } from '../../lib/errors';

export function EditClientPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: client, isLoading, isError, error, refetch } = useClient(id);
  const update = useUpdateClient();

  if (!id) {
    return <div className="mx-auto max-w-xl py-12 text-center text-muted">Cliente inválido.</div>;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 pb-10">
      <header className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate(`/identities/${id}`)}
          className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          {client?.name ?? 'Cliente'}
        </button>
        <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
          Editar cliente
        </h1>
      </header>

      {isLoading ? (
        <ClientTableSkeleton rows={3} />
      ) : isError || !client ? (
        <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
          <h2 className="text-[15px] font-semibold text-ink">
            Não conseguimos carregar esse cliente.
          </h2>
          <p className="mt-1 text-[12px] text-muted">
            {extractErrorMessage(error, 'Tente recarregar a página.')}
          </p>
          <div className="mt-4">
            <PillButton onClick={() => refetch()}>Tentar de novo</PillButton>
          </div>
        </div>
      ) : (
        <ClientForm
          editMode
          initialValues={{
            type: client.type,
            name: client.name,
            trade_name: client.trade_name,
            document: client.document,
            document_type: client.document_type,
            inscricao_municipal: client.inscricao_municipal,
            inscricao_estadual: client.inscricao_estadual,
            email: client.email,
            fiscal_email: client.fiscal_email,
            financial_email: client.financial_email,
            phone: client.phone,
            whatsapp: client.whatsapp,
            financial_whatsapp: client.financial_whatsapp,
            preferred_channel: client.preferred_channel,
            address_street: client.address_street,
            address_number: client.address_number,
            address_complement: client.address_complement,
            address_neighborhood: client.address_neighborhood,
            address_city: client.address_city,
            address_state: client.address_state,
            address_zip_code: client.address_zip_code,
            address_country: client.address_country,
            responsavel: client.responsavel,
            observations: client.observations,
            status: client.status,
            is_iss_exempt: client.is_iss_exempt,
            skip_dunning: client.skip_dunning,
          }}
          isPending={update.isPending}
          submitLabel="Salvar alterações"
          onCancel={() => navigate(`/identities/${id}`)}
          onSubmit={async (values) => {
            const payload: UpdateClientDto = {
              ...values,
              version: client.version,
            };
            try {
              await update.mutateAsync({ id, payload });
              toast.success('Alterações salvas.', { description: client.name });
              navigate(`/identities/${id}`);
            } catch (err) {
              toast.error('Não conseguimos salvar.', {
                description: extractErrorMessage(err, 'Tente de novo.'),
              });
            }
          }}
        />
      )}
    </div>
  );
}
